<template>
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" fullscreen append-to-body
        :close-on-click-modal="false" @closed="handleClosed">
        <div v-loading="loading" class="project-form-dialog-body">
            <el-form ref="form" label-width="120px">
                <!-- 基本信息 -->
                <el-card shadow="never" class="form-card">
                    <div slot="header"><span>基本信息</span></div>
                    <el-row :gutter="20">
                        <el-col :span="12">
                            <el-form-item label="防水项目名称" :required="!isViewMode">
                                <span v-if="isViewMode" class="view-text">{{ formData.name || '-' }}</span>
                                <el-input v-else v-model="formData.name" placeholder="请输入防水项目名称" />
                            </el-form-item>
                        </el-col>
                    </el-row>
                </el-card>

                <!-- 服务阶段列表 -->
                <div v-if="!loading" class="stages-container">
                    <el-collapse v-model="activeStages" class="stage-collapse">
                        <el-collapse-item v-for="(stage, stageIndex) in formData.stages" :key="stage._uid"
                            :name="stage._uid">
                            <div slot="title" class="stage-card-header">
                                <span class="stage-title">服务阶段 {{ stageIndex + 1 }}<span
                                        class="stage-subtitle">{{ stage.stageName || '未命名' }} · {{
                                            stage.formFields.length }} 个字段</span></span>
                                <el-button v-if="!isViewMode" type="danger" size="mini" plain icon="el-icon-delete"
                                    @click.stop="removeStage(stageIndex)">删除阶段</el-button>
                            </div>

                            <template v-if="renderedStages.indexOf(stage._uid) > -1">
                                <!-- 阶段属性 -->
                                <el-row :gutter="16">
                                    <el-col :xs="12" :sm="6" :md="4" :lg="3">
                                        <el-form-item label="名称" :required="!isViewMode">
                                            <span v-if="isViewMode" class="view-text">{{ stage.stageName || '-' }}</span>
                                            <el-input v-else v-model="stage.stageName" placeholder="服务阶段名称"
                                                size="small" />
                                        </el-form-item>
                                    </el-col>
                                    <el-col :xs="24" :sm="12" :md="8" :lg="4">
                                        <el-form-item label="顺序必填校验">
                                            <span v-if="isViewMode" class="view-text">{{ stage.subStageFlag === '1' ? '是' : '否'
                                                }}</span>
                                            <el-switch v-else v-model="stage.subStageFlag" active-value="1"
                                                inactive-value="0" active-text="是" inactive-text="否" />
                                        </el-form-item>
                                    </el-col>
                                    <el-col :xs="12" :sm="6" :md="4" :lg="3">
                                        <el-form-item label="别名">
                                            <span v-if="isViewMode" class="view-text">{{ stage.alias || '-' }}</span>
                                            <el-select v-else v-model="stage.alias" placeholder="请选择别名" size="small"
                                                clearable>
                                                <el-option label="基坑" value="基坑" />
                                                <el-option label="回填" value="回填" />
                                                <el-option label="面层" value="面层" />
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                    <el-col :xs="12" :sm="6" :md="4" :lg="3">
                                        <el-form-item label="排序">
                                            <span v-if="isViewMode" class="view-text">{{ stage.sort }}</span>
                                            <el-input v-else v-model.number="stage.sort" type="number" placeholder="排序"
                                                size="small" />
                                        </el-form-item>
                                    </el-col>
                                    <el-col :xs="12" :sm="6" :md="4" :lg="3">
                                        <el-form-item label="拿货价">
                                            <span v-if="isViewMode" class="view-text">{{ stage.price || '-' }}</span>
                                            <el-input v-else v-model.number="stage.price" type="number"
                                                placeholder="拿货价" size="small" />
                                        </el-form-item>
                                    </el-col>
                                    <el-col :xs="12" :sm="6" :md="4" :lg="3">
                                        <el-form-item label="阈值">
                                            <span v-if="isViewMode" class="view-text">{{ stage.threshold || '-' }}</span>
                                            <el-input v-else v-model.number="stage.threshold" type="number"
                                                placeholder="阈值" size="small" />
                                        </el-form-item>
                                    </el-col>
                                    <el-col v-if="!isViewMode" :xs="12" :sm="6" :md="4" :lg="3">
                                        <el-form-item label="分组">
                                            <el-input v-model.number="stage.costGrouping" type="number" placeholder="分组"
                                                size="small" />
                                        </el-form-item>
                                    </el-col>
                                    <el-col :xs="24" :sm="12" :md="8" :lg="4">
                                        <el-form-item label="是否展示">
                                            <span v-if="isViewMode" class="view-text">{{ stage.isShow === '1' ? '是' : '否'
                                                }}</span>
                                            <el-switch v-else v-model="stage.isShow" active-value="1" inactive-value="2"
                                                active-text="是" inactive-text="否" />
                                        </el-form-item>
                                    </el-col>
                                </el-row>

                                <!-- 表单字段配置 -->
                                <div class="fields-section">
                                    <div class="fields-header">
                                        <span class="fields-title">表单字段配置</span>
                                        <el-button v-if="!isViewMode" type="primary" size="mini" plain
                                            icon="el-icon-plus" @click="addField(stageIndex)">增加字段</el-button>
                                    </div>

                                    <!-- 查看模式：纯文本表格，不渲染任何表单控件 -->
                                    <el-table v-if="isViewMode" :data="stage.formFields" border size="small"
                                        style="width: 100%" :max-height="400" class="fields-table">
                                        <el-table-column type="index" label="序号" width="55" />
                                        <el-table-column prop="sort" label="排序" width="60" />
                                        <el-table-column prop="fieldName" label="字段名称" min-width="110" />
                                        <el-table-column label="控件类型" width="120" :formatter="fmtInputType" />
                                        <el-table-column label="数据源" width="120" :formatter="fmtDataSource" />
                                        <el-table-column label="是否展示" width="90" align="center"
                                            :formatter="fmtShowFlag" />
                                        <el-table-column label="客户端" min-width="150" :formatter="fmtClients" />
                                        <el-table-column label="是否为空" width="90" align="center"
                                            :formatter="fmtIsNull" />
                                        <el-table-column label="是否计费" width="90" align="center"
                                            :formatter="fmtCharging" />
                                        <el-table-column prop="expression" label="计费规则" width="110" />
                                        <el-table-column label="分享时可见" width="100" align="center"
                                            :formatter="fmtShareVisible" />
                                        <el-table-column prop="shareFieldName" label="客户展示内容" min-width="130" />
                                        <el-table-column label="进场标识" width="90" align="center"
                                            :formatter="fmtEntrance" />
                                        <el-table-column label="退场标识" width="90" align="center" :formatter="fmtExit" />
                                    </el-table>

                                    <!-- 编辑模式 -->
                                    <el-table v-else :data="stage.formFields" border size="small" style="width: 100%"
                                        :max-height="400" class="fields-table">
                                        <el-table-column type="index" label="序号" width="55" />
                                        <el-table-column label="排序" width="60">
                                            <template v-slot="scope">
                                                <el-input size="small" v-model="scope.row.sort" />
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="字段名称" min-width="110">
                                            <template v-slot="scope">
                                                <el-input size="small" v-model="scope.row.fieldName"
                                                    placeholder="字段名" />
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="控件类型" width="120">
                                            <template v-slot="scope">
                                                <el-select v-model="scope.row.inputType" placeholder="控件类型" size="small"
                                                    clearable @visible-change="v => v && (scope.row._itOpen = true)">
                                                    <el-option
                                                        v-for="type in (scope.row._itOpen ? inputTypes : inputTypeStub(scope.row.inputType))"
                                                        :key="type.id" :label="type.name" :value="type.id" />
                                                </el-select>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="数据源" width="120">
                                            <template v-slot="scope">
                                                <el-select v-model="scope.row.dataSource" placeholder="数据源" size="small"
                                                    clearable @visible-change="v => v && (scope.row._dsOpen = true)">
                                                    <el-option
                                                        v-for="source in (scope.row._dsOpen ? dataSources : dataSourceStub(scope.row.dataSource))"
                                                        :key="source.id" :label="source.valueName" :value="source.id" />
                                                </el-select>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="是否展示" width="120" align="center">
                                            <template v-slot="scope">
                                                <el-switch v-model="scope.row.showFlag" active-value="YES"
                                                    inactive-value="NO" active-text="是" inactive-text="否"
                                                    size="small" />
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="客户端" width="150">
                                            <template v-slot="scope">
                                                <el-select v-model="scope.row.showClient" placeholder="客户端" clearable
                                                    multiple size="small">
                                                    <el-option v-for="client in typeClients" :key="client.value"
                                                        :label="client.label" :value="client.value" />
                                                </el-select>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="是否为空" width="120" align="center">
                                            <template v-slot="scope">
                                                <el-switch v-model="scope.row.isNull" active-value="YES"
                                                    inactive-value="NO" active-text="是" inactive-text="否"
                                                    size="small" />
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="是否计费" width="120" align="center">
                                            <template v-slot="scope">
                                                <el-switch v-model="scope.row.charging" active-value="YES"
                                                    active-text="是" inactive-text="否" inactive-value="NO"
                                                    size="small" />
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="计费规则" width="110">
                                            <template v-slot="scope">
                                                <el-input size="small" v-model="scope.row.expression" placeholder="计费规则"
                                                    clearable />
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="分享时可见" width="120" align="center">
                                            <template v-slot="scope">
                                                <el-switch v-model="scope.row.shareVisible" active-value="Y"
                                                    inactive-value="N" active-text="是" inactive-text="否"
                                                    size="small" />
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="客户展示内容" min-width="130">
                                            <template v-slot="scope">
                                                <el-input size="small" v-model="scope.row.shareFieldName"
                                                    placeholder="客户展示内容" />
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="进场标识" width="110" align="center">
                                            <template v-slot="scope">
                                                <el-select v-model="scope.row.isEntrance" size="small">
                                                    <el-option label="否" :value="0" />
                                                    <el-option label="是" :value="1" />
                                                </el-select>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="退场标识" width="110" align="center">
                                            <template v-slot="scope">
                                                <el-select v-model="scope.row.isExit" size="small">
                                                    <el-option label="否" :value="0" />
                                                    <el-option label="是" :value="1" />
                                                </el-select>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="操作" width="80" align="center" fixed="right">
                                            <template v-slot="scope">
                                                <el-button type="text" size="small"
                                                    @click="removeField(stageIndex, scope.$index)"
                                                    icon="el-icon-delete" />
                                            </template>
                                        </el-table-column>
                                    </el-table>
                                </div>
                            </template>
                        </el-collapse-item>
                    </el-collapse>

                    <!-- 增加阶段按钮 -->
                    <div v-if="!isViewMode" class="add-stage-btn">
                        <el-button type="primary" icon="el-icon-plus" @click="addStage">增加阶段</el-button>
                    </div>
                </div>
            </el-form>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button @click="handleCancel">关闭</el-button>
            <el-button v-if="!isViewMode" type="primary" @click="submit" :loading="submitLoading">
                {{ mode === 'add' ? '确认新增' : '确认修改' }}
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import {
    findWaterProofDynamicId,
    findDataSourceAll,
    findDataSourceFields,
    editWaterProofDynamic,
    addDynamic
} from '@/api/work/waterProof/projectDynamic'

const EMPTY_STAGE = {
    stageName: '',
    alias: '',
    price: '',
    threshold: '',
    sort: '',
    costGrouping: '',
    isShow: '1',
    subStageFlag: '0',
    id: '',
    formFields: []
}

const EMPTY_FIELD = {
    fieldName: '',
    shareFieldName: '',
    inputType: '',
    dataSource: '',
    isEntrance: 0,
    isExit: 0,
    showClient: [],
    showFlag: 'YES',
    isNull: 'NO',
    charging: 'NO',
    expression: '',
    sort: '',
    shareVisible: 'N',
    // 下拉选项懒渲染标记，提交前会被 omitInternal 剔除
    _dsOpen: false,
    _itOpen: false
}

let uidSeed = 0
const nextUid = () => ++uidSeed

const omitInternal = obj => {
    const result = {}
    Object.keys(obj).forEach(key => {
        if (key.charAt(0) !== '_') result[key] = obj[key]
    })
    return result
}

export default {
    name: 'ProjectFormDialog',
    props: {
        visible: { type: Boolean, default: false },
        mode: { type: String, default: 'add' }, // add | edit | view
        projectId: { type: String, default: '' },
        agentId: { type: String, default: '' }
    },
    data() {
        return {
            dialogVisible: false,
            loading: false,
            submitLoading: false,
            formData: {
                name: '',
                stages: []
            },
            typeClients: [
                { label: '工程师端', value: 'JOINS_ENGINEER' },
                { label: '消费者端', value: 'JOINS_USER' },
                { label: '商户端', value: 'JOINS_MERCHANT' }
            ],
            inputTypes: [],
            dataSources: [],
            additionalFields: {},
            activeStages: [],
            renderedStages: []
        }
    },
    computed: {
        dialogTitle() {
            const map = { add: '新增防水项目', edit: '编辑防水项目', view: '查看防水项目' }
            return map[this.mode] || '防水项目'
        },
        isViewMode() {
            return this.mode === 'view'
        },
        inputTypeMap() {
            const map = {}
            this.inputTypes.forEach(item => { map[item.id] = item })
            return map
        },
        dataSourceMap() {
            const map = {}
            this.dataSources.forEach(item => { map[item.id] = item })
            return map
        },
        clientLabelMap() {
            const map = {}
            this.typeClients.forEach(item => { map[item.value] = item.label })
            return map
        }
    },
    watch: {
        visible: {
            handler(val) {
                this.dialogVisible = val
                if (val) this.open()
            },
            immediate: true
        },
        activeStages(val) {
            const names = Array.isArray(val) ? val : [val]
            names.forEach(name => {
                if (this.renderedStages.indexOf(name) === -1) this.renderedStages.push(name)
            })
        }
    },
    methods: {
        async open() {
            this.loading = true
            this.submitLoading = false
            this.formData = { name: '', stages: [] }
            this.activeStages = []
            this.renderedStages = []

            try {
                // 并行加载控件类型和数据源
                const [fieldsRes, sourceRes] = await Promise.all([
                    findDataSourceFields(),
                    findDataSourceAll()
                ])
                this.inputTypes = JSON.parse(fieldsRes.data)
                this.dataSources = JSON.parse(sourceRes.data).rows

                if (this.mode === 'add') {
                    this.initEmptyForm()
                } else {
                    await this.loadProjectDetail()
                }
                this.expandFirstStage()
            } catch (e) {
                console.error('初始化加载失败:', e)
                this.$message.error('数据加载失败')
            } finally {
                this.loading = false
            }
        },

        // 只展开第一个阶段，其余阶段的字段表格在展开时才渲染
        expandFirstStage() {
            const first = this.formData.stages[0]
            this.activeStages = first ? [first._uid] : []
            this.renderedStages = first ? [first._uid] : []
        },

        createStage() {
            return { ...EMPTY_STAGE, _uid: nextUid(), formFields: [{ ...EMPTY_FIELD }] }
        },

        initEmptyForm() {
            this.formData = {
                name: '',
                stages: [this.createStage()]
            }
            this.additionalFields = {}
        },

        async loadProjectDetail() {
            if (!this.projectId) {
                this.$message.error('缺少项目ID')
                return
            }
            try {
                const res = await findWaterProofDynamicId(this.projectId)
                if (res && (res.code === '200' || res.code === 200)) {
                    const parsedData = JSON.parse(res.data)
                    const data = parsedData.rows && parsedData.rows.length > 0 ? parsedData.rows[0] : parsedData
                    if (!data) {
                        this.$message.error('未找到项目数据')
                        return
                    }
                    this.formData.name = data.name || ''
                    this.additionalFields = { ...data }

                    if (data.list && Array.isArray(data.list)) {
                        const inputTypeMap = this.inputTypeMap
                        this.formData.stages = data.list.map(stage => ({
                            ...stage,
                            _uid: nextUid(),
                            stageName: stage.name || '',
                            alias: stage.alias || '',
                            price: stage.price || '',
                            threshold: stage.threshold || '',
                            sort: stage.sort || 0,
                            costGrouping: stage.costGrouping || '',
                            isShow: String(stage.isShow || '1'),
                            subStageFlag: stage.subStageFlag !== undefined && stage.subStageFlag !== null
                                ? String(stage.subStageFlag) : '0',
                            id: stage.id || '',
                            formFields: (stage.dynamicFormRequest && stage.dynamicFormRequest.fields
                                ? stage.dynamicFormRequest.fields.map(field => ({
                                    ...field,
                                    id: field.id || '',
                                    fieldName: field.fieldName || '',
                                    shareFieldName: field.shareFieldName || '',
                                    inputType: inputTypeMap[field.inputType] ? inputTypeMap[field.inputType].id : '',
                                    dataSource: field.dataSource || '',
                                    isEntrance: Number(field.isEntrance || 0),
                                    isExit: Number(field.isExit || 0),
                                    isNull: field.isNull || 'NO',
                                    showFlag: field.showFlag || 'YES',
                                    showClient: field.showClient ? field.showClient.split(',') : [],
                                    charging: field.charging || 'NO',
                                    expression: field.expression || '',
                                    sort: field.sort || 0,
                                    shareVisible: field.shareVisible || 'N',
                                    _dsOpen: false,
                                    _itOpen: false
                                }))
                                : [])
                        }))
                    } else {
                        this.formData.stages = []
                    }
                } else {
                    this.$message.error(res.message || '获取项目详情失败')
                }
            } catch (e) {
                console.error('获取项目详情失败:', e)
                this.$message.error('获取项目详情失败')
            }
        },

        addStage() {
            const stage = this.createStage()
            this.formData.stages.push(stage)
            this.activeStages.push(stage._uid)
            this.renderedStages.push(stage._uid)
        },

        removeStage(index) {
            const uid = this.formData.stages[index]._uid
            this.formData.stages.splice(index, 1)
            this.activeStages = this.activeStages.filter(name => name !== uid)
            this.renderedStages = this.renderedStages.filter(name => name !== uid)
        },

        addField(stageIndex) {
            this.formData.stages[stageIndex].formFields.push({ ...EMPTY_FIELD })
        },

        removeField(stageIndex, fieldIndex) {
            this.formData.stages[stageIndex].formFields.splice(fieldIndex, 1)
        },

        // 下拉未展开时只渲染已选中的那一项，避免每行都实例化全量 el-option
        inputTypeStub(id) {
            const hit = this.inputTypeMap[id]
            return hit ? [hit] : []
        },

        dataSourceStub(id) {
            const hit = this.dataSourceMap[id]
            return hit ? [hit] : []
        },

        fmtInputType(row) {
            const hit = this.inputTypeMap[row.inputType]
            return hit ? hit.name : '-'
        },

        fmtDataSource(row) {
            const hit = this.dataSourceMap[row.dataSource]
            return hit ? hit.valueName : '-'
        },

        fmtClients(row) {
            if (!row.showClient || !row.showClient.length) return '-'
            return row.showClient.map(v => this.clientLabelMap[v] || v).join('、')
        },

        fmtShowFlag(row) {
            return row.showFlag === 'YES' ? '是' : '否'
        },

        fmtIsNull(row) {
            return row.isNull === 'YES' ? '是' : '否'
        },

        fmtCharging(row) {
            return row.charging === 'YES' ? '是' : '否'
        },

        fmtShareVisible(row) {
            return row.shareVisible === 'Y' ? '是' : '否'
        },

        fmtEntrance(row) {
            return Number(row.isEntrance) === 1 ? '是' : '否'
        },

        fmtExit(row) {
            return Number(row.isExit) === 1 ? '是' : '否'
        },

        buildSubmitParams() {
            return {
                ...this.additionalFields,
                id: this.projectId,
                agentId: this.agentId,
                name: this.formData.name,
                isEntrance: this.formData.isEntrance,
                isExit: this.formData.isExit,
                type: 'WATERPROOF_PROJECT',
                list: this.formData.stages.map(stage => ({
                    ...omitInternal(stage),
                    name: stage.stageName,
                    alias: stage.alias || '',
                    price: stage.price,
                    threshold: stage.threshold,
                    sort: stage.sort,
                    costGrouping: stage.costGrouping,
                    isShow: stage.isShow,
                    subStageFlag: stage.subStageFlag || '0',
                    type: 'WATERPROOF_PROJECT',
                    id: stage.id ? stage.id : '',
                    formFields: stage.formFields.map(field => omitInternal(field)),
                    dynamicFormRequest: {
                        ...stage.dynamicFormRequest,
                        fields: stage.formFields.map(field => ({
                            ...omitInternal(field),
                            id: field.id ? field.id : '',
                            fieldName: field.fieldName || '',
                            inputType: field.inputType || '',
                            dataSource: field.dataSource || '',
                            isEntrance: Number(field.isEntrance || 0),
                            isExit: Number(field.isExit || 0),
                            isNull: field.isNull || 'NO',
                            showClient: field.showClient.join(','), // 将数组转换为逗号分隔的字符串
                            charging: field.charging || 'NO',
                            expression: field.expression || '',
                            sort: field.sort || 0,
                            shareVisible: field.shareVisible || 'N',
                            shareFieldName: field.shareFieldName || '',
                            // picUrl: field.picUrl.map(url => (typeof url === 'string' ? url : url.url)).join(','),
                        }))
                    }
                }))
            }
        },

        async submit() {
            if (!this.formData.name) {
                this.$message.warning('请输入防水项目名称')
                return
            }
            this.submitLoading = true
            const params = this.buildSubmitParams()
            try {
                const api = this.mode === 'add' ? addDynamic : editWaterProofDynamic
                const res = await api(params)
                if (res.code === '200') {
                    this.$message.success(this.mode === 'add' ? '新增成功' : '修改成功')
                    this.$emit('submit-success')
                    this.dialogVisible = false
                    this.$emit('update:visible', false)
                    this.$emit('close')
                } else {
                    this.$message.error(res.message || (this.mode === 'add' ? '新增失败' : '修改失败'))
                }
            } catch (e) {
                this.$message.error('请求失败: ' + (e.message || '未知错误'))
            } finally {
                this.submitLoading = false
            }
        },

        handleCancel() {
            this.dialogVisible = false
            this.$emit('update:visible', false)
            this.$emit('close')
        },

        handleClosed() {
            this.$emit('update:visible', false)
            this.$emit('close')
        }
    }
}
</script>

<style lang="scss" scoped>
.project-form-dialog-body {
    height: 100%;
    overflow-y: auto;
    padding: 4px 0;
}

.form-card {
    margin-bottom: 16px;

    ::v-deep .el-card__header {
        padding: 10px 16px;
        font-weight: 600;
        background: #fafafa;
        border-bottom: 1px solid #ebeef5;
    }

    ::v-deep .el-card__body {
        padding: 16px;
    }
}

.stage-collapse {
    border-top: none;

    ::v-deep .el-collapse-item__header {
        height: auto;
        min-height: 48px;
        line-height: 1.5;
        padding: 10px 16px;
        font-weight: 600;
        background: #fafafa;
        border-bottom: 1px solid #ebeef5;
    }

    ::v-deep .el-collapse-item__arrow {
        flex-shrink: 0;
    }

    ::v-deep .el-collapse-item__content {
        padding: 16px;
    }

    ::v-deep .el-collapse-item__wrap {
        border-bottom: 1px solid #ebeef5;
    }
}

.stage-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    min-width: 0;
}

.stage-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.stage-card-header .el-button {
    flex-shrink: 0;
    margin-left: 12px;
}

.stage-subtitle {
    margin-left: 8px;
    font-weight: 400;
    font-size: 12px;
    color: #909399;
}

.view-text {
    display: inline-block;
    line-height: 32px;
    color: #303133;
    word-break: break-all;
}

.fields-section {
    margin-top: 12px;
    border-top: 1px dashed #e8e8e8;
    padding-top: 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.fields-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.fields-title {
    font-size: 13px;
    font-weight: 600;
    color: #606266;
}

.fields-table {
    ::v-deep .cell {
        white-space: nowrap;
    }

    ::v-deep .el-switch {
        margin: 0;
    }
}

.add-stage-btn {
    text-align: center;
    margin-top: 16px;
    padding: 16px;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: #409eff;
        background: #ecf5ff;
    }
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
